import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaUser, FaEnvelope, FaCalendar, FaDollarSign, FaBox, FaCopy } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import orderService from '../services/orderService';

function OrderDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000/static/';

  const truncateTransactionId = (txId) => {
    if (!txId || txId.length <= 12) return txId;
    return `${txId.substring(0, 5)}...${txId.substring(txId.length - 5)}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${STORAGE_URL}${imagePath}`;
  };

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getById(id);
      setOrder(data);
    } catch (err) {
      setError('Failed to load order details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdating(true);
      await orderService.updateStatus(id, newStatus);
      fetchOrder();
    } catch (err) {
      setError('Failed to update order status');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusVariant = (status) => {
    const variants = {
      completed: 'success',
      pending: 'warning',
      canceled: 'danger'
    };
    return variants[status] || 'secondary';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!order) {
    return (
      <Container fluid className="px-4">
        <Alert variant="danger">Order not found</Alert>
        <Button variant="secondary" onClick={() => navigate('/orders')}>
          <FaArrowLeft className="me-2" />
          Back to Orders
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title={`Order #${order.id}`}
        subtitle={`Placed on ${new Date(order.created_at).toLocaleDateString()}`}
      />

      <Row className="mb-3">
        <Col>
          <Button variant="secondary" onClick={() => navigate('/orders')}>
            <FaArrowLeft className="me-2" />
            Back to Orders
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row className="g-4">
        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-semibold mb-0">Order Items</h5>
                <Badge bg={getStatusVariant(order.status)} pill className="px-3 py-2">
                  {order.display_status}
                </Badge>
              </div>

              {order.items && order.items.length > 0 ? (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="fw-semibold">Product</th>
                        <th className="fw-semibold text-center">Quantity</th>
                        <th className="fw-semibold text-end">Price</th>
                        <th className="fw-semibold text-end">Subtotal</th>
                        <th className="fw-semibold text-center">Downloads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              {item.product?.image ? (
                                <img
                                  src={getImageUrl(item.product.image)}
                                  alt={item.product.title}
                                  className="rounded me-3"
                                  width="48"
                                  height="48"
                                  style={{ objectFit: 'cover' }}
                                />
                              ) : (
                                <div
                                  className="rounded me-3 bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold"
                                  style={{ width: '48px', height: '48px' }}
                                >
                                  {item.product?.title?.charAt(0) || 'P'}
                                </div>
                              )}
                              <div>
                                <div className="fw-semibold">{item.product?.title || 'Unknown Product'}</div>
                                {item.product?.product_type && (
                                  <small className="text-muted">
                                    {item.product.product_type === 'deal' ? '📦 Deal' : 
                                     item.product.product_type === 'offer' ? '🎫 Offer' : 
                                     '⏰ Limited Offer'}
                                  </small>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-center align-middle">{item.quantity}</td>
                          <td className="text-end align-middle">${item.price.toFixed(2)}</td>
                          <td className="text-end align-middle fw-semibold">${item.subtotal.toFixed(2)}</td>
                          <td className="text-center align-middle">
                            {item.downloads ? (
                              <small className="text-muted">
                                {item.downloads.used} / {item.downloads.used + item.downloads.remaining}
                              </small>
                            ) : (
                              <small className="text-muted">N/A</small>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  No items found in this order
                </Alert>
              )}
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h5 className="fw-semibold mb-3">Order Status</h5>
              <div className="d-flex gap-2">
                <Button
                  variant={order.status === 'pending' ? 'warning' : 'outline-warning'}
                  onClick={() => handleStatusChange('pending')}
                  disabled={updating || order.status === 'pending'}
                >
                  Pending
                </Button>
                <Button
                  variant={order.status === 'completed' ? 'success' : 'outline-success'}
                  onClick={() => handleStatusChange('completed')}
                  disabled={updating || order.status === 'completed'}
                >
                  Completed
                </Button>
                <Button
                  variant={order.status === 'canceled' ? 'danger' : 'outline-danger'}
                  onClick={() => handleStatusChange('canceled')}
                  disabled={updating || order.status === 'canceled'}
                >
                  Canceled
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <h5 className="fw-semibold mb-3">Customer Information</h5>
              {order.user ? (
                <>
                  <div className="d-flex align-items-center mb-2">
                    <FaUser className="text-muted me-2" />
                    <span>{order.user.username}</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <FaEnvelope className="text-muted me-2" />
                    <span>{order.user.email}</span>
                  </div>
                </>
              ) : (
                <p className="text-muted mb-0">User ID: {order.user_id}</p>
              )}
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <h5 className="fw-semibold mb-3">Order Summary</h5>
              <div className="d-flex align-items-center mb-2">
                <FaCalendar className="text-muted me-2" />
                <small className="text-muted">Created:</small>
                <span className="ms-auto">{new Date(order.created_at).toLocaleString()}</span>
              </div>
              <div className="d-flex align-items-center mb-2">
                <FaBox className="text-muted me-2" />
                <small className="text-muted">Items:</small>
                <span className="ms-auto">{order.items?.length || 0}</span>
              </div>
              <hr />
              <div className="d-flex align-items-center">
                <FaDollarSign className="text-muted me-2" />
                <strong>Total:</strong>
                <h5 className="ms-auto mb-0 text-primary">${order.display_total}</h5>
              </div>
            </Card.Body>
          </Card>

          {order.payment && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h5 className="fw-semibold mb-3">Payment Information</h5>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small className="text-muted">Transaction ID:</small>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold">{truncateTransactionId(order.payment.transaction_id)}</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-muted"
                      onClick={() => copyToClipboard(order.payment.transaction_id)}
                      title="Copy full transaction ID"
                    >
                      <FaCopy size={14} />
                    </Button>
                  </div>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <small className="text-muted">Payment Method:</small>
                  <Badge bg="info" className="text-dark">
                    {order.payment.method_display}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between">
                  <small className="text-muted">Payment Status:</small>
                  <Badge bg={order.payment.payment_status === 'successful' ? 'success' : 'warning'}>
                    {order.payment.display_status}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          )}

          {order.cart_id && (
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h5 className="fw-semibold mb-3">Additional Info</h5>
                <div className="d-flex justify-content-between">
                  <small className="text-muted">Cart ID:</small>
                  <span>#{order.cart_id}</span>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default OrderDetailsPage;
